import { useState } from "react";
import { useTranslation } from "react-i18next";

const TAB_IDS = ["tab1", "tab2", "tab3", "tab4", "tab5", "tab6"] as const;

function ContentBlock({ text }: { text: string }) {
  return (
    <p className="mb-3 text-black last:mb-0 jandi-guidance-content" style={{ whiteSpace: "pre-line" }}>
      {text}
    </p>
  );
}

function SectionTitle({ text }: { text: string }) {
  return (
    <p className="mt-4 mb-1 font-semibold text-gray-800 jandi-guidance-title first:mt-0" style={{ fontSize: '15px' }}>
      {text}
    </p>
  );
}

export default function ProviderGuidanceTabs() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<(typeof TAB_IDS)[number]>("tab1");

  return (
    <div className="w-full max-w-6xl mx-auto mt-6 text-gray-700 px-2 jandi-guidance-page">
      {/* --- Tab Buttons --- */}
      <div className="flex flex-col gap-2 mb-4">
        <div className="bg-gray-100 rounded-xl p-1 flex flex-nowrap gap-1">
          {TAB_IDS.map((tabId) => (
            <button
              key={tabId}
              onClick={() => setActiveTab(tabId)}
              className={`jandi-guidance-tabs px-2 py-3 rounded-lg transition-all duration-200 font-medium text-left flex-1 min-w-0 max-w-[180px] ${
                activeTab === tabId
                  ? "bg-white shadow text-[#4285F4] border-b-2 border-[#4285F4]"
                  : "text-gray-600 hover:text-[#4285F4] border-b-2 border-transparent"
              }`}
            >
              {t(`providerGuidance_${tabId}_title`)}
            </button>
          ))}
        </div>
      </div>

      {/* --- Tab Content --- */}
      <div className="bg-white shadow-sm rounded-2xl p-6 leading-relaxed border jandi-guidance-content">
        {activeTab === "tab1" && (
          <div className="space-y-1">
            <ContentBlock text={t("providerGuidance_tab1_intro")} />
            <SectionTitle text={t("providerGuidance_tab1_mandatoryTitle")} />
            <ContentBlock text={t("providerGuidance_tab1_mandatory")} />
            <SectionTitle text={t("providerGuidance_tab1_otherDocsTitle")} />
            <ContentBlock text={t("providerGuidance_tab1_otherDocs")} />
            <SectionTitle text={t("providerGuidance_tab1_profileTitle")} />
            <ContentBlock text={t("providerGuidance_tab1_profile")} />
            <SectionTitle text={t("providerGuidance_tab1_photoTitle")} />
            <ContentBlock text={t("providerGuidance_tab1_photoDo")} />
            <ContentBlock text={t("providerGuidance_tab1_photoDont")} />
            <ContentBlock text={t("providerGuidance_tab1_photoNote")} />
          </div>
        )}

        {activeTab === "tab2" && (
          <div className="space-y-1">
            <SectionTitle text={t("providerGuidance_tab2_whereTitle")} />
            <ContentBlock text={t("providerGuidance_tab2_where")} />
            <SectionTitle text={t("providerGuidance_tab2_timeTitle")} />
            <ContentBlock text={t("providerGuidance_tab2_time")} />
            <SectionTitle text={t("providerGuidance_tab2_notifTitle")} />
            <ContentBlock text={t("providerGuidance_tab2_notif")} />
            <SectionTitle text={t("providerGuidance_tab2_coordTitle")} />
            <ContentBlock text={t("providerGuidance_tab2_coord")} />
            <SectionTitle text={t("providerGuidance_tab2_chatTitle")} />
            <ContentBlock text={t("providerGuidance_tab2_chat")} />
          </div>
        )}

        {activeTab === "tab3" && (
          <div className="space-y-1">
            <SectionTitle text={t("providerGuidance_tab3_step1Title")} />
            <ContentBlock text={t("providerGuidance_tab3_step1")} />
            <SectionTitle text={t("providerGuidance_tab3_step2Title")} />
            <ContentBlock text={t("providerGuidance_tab3_step2")} />
            <SectionTitle text={t("providerGuidance_tab3_step3Title")} />
            <ContentBlock text={t("providerGuidance_tab3_step3")} />
            <SectionTitle text={t("providerGuidance_tab3_step4Title")} />
            <ContentBlock text={t("providerGuidance_tab3_step4")} />
            <SectionTitle text={t("providerGuidance_tab3_step5Title")} />
            <ContentBlock text={t("providerGuidance_tab3_step5")} />
            <SectionTitle text={t("providerGuidance_tab3_step6Title")} />
            <ContentBlock text={t("providerGuidance_tab3_step6")} />
          </div>
        )}

        {activeTab === "tab4" && (
          <div className="space-y-1">
            <SectionTitle text={t("providerGuidance_tab4_whenPaidTitle")} />
            <ContentBlock text={t("providerGuidance_tab4_whenPaid")} />
            <SectionTitle text={t("providerGuidance_tab4_clientCancelTitle")} />
            <ContentBlock text={t("providerGuidance_tab4_clientCancel")} />
            <SectionTitle text={t("providerGuidance_tab4_providerCancel24Title")} />
            <ContentBlock text={t("providerGuidance_tab4_providerCancel24")} />
            <SectionTitle text={t("providerGuidance_tab4_providerCancelWithinTitle")} />
            <ContentBlock text={t("providerGuidance_tab4_providerCancelWithin")} />
            <SectionTitle text={t("providerGuidance_tab4_reputationTitle")} />
            <ContentBlock text={t("providerGuidance_tab4_reputation")} />
            <SectionTitle text={t("providerGuidance_tab4_multiProviderTitle")} />
            <ContentBlock text={t("providerGuidance_tab4_multiProvider")} />
          </div>
        )}

        {activeTab === "tab5" && (
          <div className="space-y-1">
            <ContentBlock text={t("providerGuidance_tab5_standards")} />
            <ContentBlock text={t("providerGuidance_tab5_platformOnly")} />
          </div>
        )}

        {activeTab === "tab6" && (
          <div className="space-y-1">
            <ContentBlock text={t("providerGuidance_tab6_contractor")} />
            <ContentBlock text={t("providerGuidance_tab6_payments")} />
            <ContentBlock text={t("providerGuidance_tab6_docs")} />
            <p className="mt-3">
              <a
                href="https://www.jandipr.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4285F4] underline"
              >
                www.jandipr.com
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
